/**
 * pluginContext.ts - PluginContext factory for FMwvTool
 * Integrates Pluggr with OpCentral orchestrators
 * 
 * CHANGE HISTORY:
 * - 251114_2050 [complete] danielbeer: Added menu registration API for SideMenu integration
 * - 251107_2235 [complete] danielbeer: Initial creation - Pluggr integration
 * 
 * INTEGRATION:
 * - Interfacr: UI operations (views, notifications, dialogs) + Menu components
 * - Aggregatr: Storage operations (get, set, delete)
 * - Actr: Action execution and logging
 * - Loggr: Logging and debugging
 */

import type { PluginContext, Aggregatr, MenuItem } from '@opcentral/dependr';
import { getLoggr, ConsoleAdapter } from '@opcentral/dependr';
import { getFileMakerBridge } from './filemaker/FileMakerBridge';

// Menu item registry for plugins
type MenuItemCallback = (item: MenuItem) => void;
const menuItemRegistry = new Map<string, MenuItem[]>();
const menuItemCallbacks = new Set<MenuItemCallback>();

export function registerMenuItemCallback(callback: MenuItemCallback) {
  menuItemCallbacks.add(callback);
}

export function unregisterMenuItemCallback(callback: MenuItemCallback) {
  menuItemCallbacks.delete(callback);
}

export function getPluginMenuItems(menuId: string = 'side'): MenuItem[] {
  return menuItemRegistry.get(menuId) || [];
}

function notifyMenuItemChange(item: MenuItem) {
  menuItemCallbacks.forEach(callback => {
    try {
      callback(item);
    } catch (error) {
      console.error('Error in menu item callback:', error);
    }
  });
}

// Simple event emitter for plugin events
class PluginEventEmitter {
  private listeners = new Map<string, Set<Function>>();

  on(event: string, handler: Function) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(handler);
  }

  off(event: string, handler: Function) {
    this.listeners.get(event)?.delete(handler);
  }

  emit(event: string, data?: any) {
    this.listeners.get(event)?.forEach(handler => {
      try {
        handler(data);
      } catch (error) {
        console.error(`Error in event handler for ${event}:`, error);
      }
    });
  }
}

const globalEventEmitter = new PluginEventEmitter();

/**
 * Create PluginContext for a specific plugin
 * Provides sandboxed access to OpCentral orchestrators
 */
export function createFMwvToolContext(
  pluginId: string,
  aggregatr?: Aggregatr | null
): PluginContext {
  const loggr = getLoggr({ 
    category: pluginId,
    adapters: [new ConsoleAdapter({ minLevel: 'debug' })]
  });
  const fmBridge = getFileMakerBridge();

  return {
    // FileMaker integration
    registerFunction: (name: string, fn: (...args: any[]) => any) => {
      loggr.info(`[${pluginId}] Registering function:`, name);
      fmBridge.registerFunction(name, fn);
    },

    registerCommand: (command: { id: string; name: string; description?: string; execute: (params?: any) => any }) => {
      loggr.info(`[${pluginId}] Registering command:`, command.id);
      // Store command for later execution
      (window as any)[`cmd_${command.id}`] = command.execute;
    },

    // UI operations via Interfacr
    ui: {
      createView: (config: any) => {
        loggr.info(`[${pluginId}] Creating view:`, config);
        // TODO: Integrate with Interfacr
        // const interfacr = useInterfacr();
        // return interfacr.createView(config);
        return null as any;
      },

      showNotification: (message: string, type?: 'info' | 'success' | 'warning' | 'error') => {
        loggr.info(`[${pluginId}] Notification:`, message);
        // TODO: Integrate with Interfacr/Notifyr
        // const interfacr = useInterfacr();
        // interfacr.showNotification({ message, type });
        console.log(`[${pluginId}] ${type || 'info'}: ${message}`);
      },

      showDialog: async (config: any) => {
        loggr.info(`[${pluginId}] Showing dialog:`, config);
        // TODO: Integrate with Interfacr
        return null as any;
      },

      createPanel: (config: any) => {
        loggr.info(`[${pluginId}] Creating panel:`, config);
        // TODO: Integrate with Interfacr/Panelr
        return null as any;
      }
    },

    // Storage operations via Aggregatr
    storage: {
      get: async (key: string) => {
        const storageKey = `plugin_${pluginId}_${key}`;
        loggr.debug(`[${pluginId}] Storage get:`, storageKey);
        
        if (aggregatr) {
          try {
            const result = await aggregatr.read('plugin_storage', storageKey);
            return result?.data || null;
          } catch (error) {
            loggr.warn('Aggregatr read failed, falling back to localStorage', 'storage', { error });
          }
        }
        
        // Fallback to localStorage
        const value = localStorage.getItem(storageKey);
        return value ? JSON.parse(value) : null;
      },

      set: async (key: string, value: any) => {
        const storageKey = `plugin_${pluginId}_${key}`;
        loggr.debug(`[${pluginId}] Storage set:`, storageKey);
        
        if (aggregatr) {
          try {
            await aggregatr.update('plugin_storage', storageKey, { data: value });
            return;
          } catch (error) {
            loggr.warn('Aggregatr update failed, falling back to localStorage', 'storage', { error });
          }
        }
        
        // Fallback to localStorage
        localStorage.setItem(storageKey, JSON.stringify(value));
      },

      delete: async (key: string) => {
        const storageKey = `plugin_${pluginId}_${key}`;
        loggr.debug(`[${pluginId}] Storage delete:`, storageKey);
        
        if (aggregatr) {
          try {
            await aggregatr.delete('plugin_storage', storageKey);
            return;
          } catch (error) {
            loggr.warn(`[${pluginId}] Aggregatr delete failed, falling back to localStorage`, error);
          }
        }
        
        // Fallback to localStorage
        localStorage.removeItem(storageKey);
      },

      list: async (prefix?: string) => {
        const storagePrefix = `plugin_${pluginId}_${prefix || ''}`;
        loggr.debug('Storage list', 'storage', { prefix: storagePrefix });
        
        if (aggregatr) {
          try {
            const results = await aggregatr.query('plugin_storage', { prefix: storagePrefix });
            return results.map(r => r.id.replace(`plugin_${pluginId}_`, ''));
          } catch (error) {
            loggr.warn('Aggregatr query failed, falling back to localStorage', 'storage', { error });
          }
        }
        
        // Fallback to localStorage
        const keys: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.startsWith(storagePrefix)) {
            keys.push(key.replace(`plugin_${pluginId}_`, ''));
          }
        }
        return keys;
      }
    },

    // Event system
    events: {
      on: (event: string, handler: Function) => {
        const scopedEvent = `${pluginId}:${event}`;
        loggr.debug(`[${pluginId}] Registering event handler:`, scopedEvent);
        globalEventEmitter.on(scopedEvent, handler);
      },

      off: (event: string, handler: Function) => {
        const scopedEvent = `${pluginId}:${event}`;
        loggr.debug(`[${pluginId}] Removing event handler:`, scopedEvent);
        globalEventEmitter.off(scopedEvent, handler);
      },

      emit: (event: string, data?: any) => {
        const scopedEvent = `${pluginId}:${event}`;
        loggr.debug(`[${pluginId}] Emitting event:`, scopedEvent, data);
        globalEventEmitter.emit(scopedEvent, data);
        
        // Also emit global plugin event
        globalEventEmitter.emit('plugin:event', { pluginId, event, data });
      }
    },

    // Logging
    log: {
      info: (message: string, ...args: any[]) => {
        loggr.info(message, 'plugin', { args });
      },
      warn: (message: string, ...args: any[]) => {
        loggr.warn(message, 'plugin', { args });
      },
      error: (message: string, ...args: any[]) => {
        loggr.error(message, 'plugin', { args });
      },
      debug: (message: string, ...args: any[]) => {
        loggr.debug(message, 'plugin', { args });
      }
    },

    // Plugin metadata
    plugin: {
      id: pluginId,
      getConfig: async () => {
        const config = localStorage.getItem(`plugin_${pluginId}_config`);
        return config ? JSON.parse(config) : {};
      },
      setConfig: async (config: any) => {
        localStorage.setItem(`plugin_${pluginId}_config`, JSON.stringify(config));
      }
    },

    // Menu registration (Interfacr SideMenu/TopMenu integration)
    menu: {
      registerItem: (menuId: string, item: MenuItem) => {
        loggr.info(`[${pluginId}] Registering menu item:`, menuId, item.id);
        
        // Add plugin metadata to item
        const pluginItem: MenuItem = {
          ...item,
          metadata: {
            ...item.metadata,
            pluginId,
            source: 'plugin'
          }
        };
        
        // Add to registry
        if (!menuItemRegistry.has(menuId)) {
          menuItemRegistry.set(menuId, []);
        }
        menuItemRegistry.get(menuId)!.push(pluginItem);
        
        // Notify listeners
        notifyMenuItemChange(pluginItem);
      },
      
      unregisterItem: (menuId: string, itemId: string) => {
        loggr.info(`[${pluginId}] Unregistering menu item:`, menuId, itemId);
        
        const items = menuItemRegistry.get(menuId);
        if (items) {
          const index = items.findIndex(i => i.id === itemId && i.metadata?.pluginId === pluginId);
          if (index !== -1) {
            const [removed] = items.splice(index, 1);
            notifyMenuItemChange(removed);
          }
        }
      },
      
      updateItem: (menuId: string, itemId: string, updates: Partial<MenuItem>) => {
        loggr.info(`[${pluginId}] Updating menu item:`, menuId, itemId);
        
        const items = menuItemRegistry.get(menuId);
        if (items) {
          const item = items.find(i => i.id === itemId && i.metadata?.pluginId === pluginId);
          if (item) {
            Object.assign(item, updates);
            notifyMenuItemChange(item);
          }
        }
      },
      
      getItems: (menuId: string) => {
        return menuItemRegistry.get(menuId)?.filter(i => i.metadata?.pluginId === pluginId) || [];
      }
    }
  };
}

/**
 * Enhanced context with Actr integration
 * For plugins that need action orchestration
 */
export function createEnhancedContext(pluginId: string, actr: any): PluginContext {
  const baseContext = createFMwvToolContext(pluginId);

  return {
    ...baseContext,
    
    // Add Actr integration
    actions: {
      execute: async (actionName: string, params: any) => {
        return await actr.execute(actionName, params);
      },
      register: (actionName: string, handler: any) => {
        actr.register(actionName, { handler });
      }
    }
  };
}
